import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Tag, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { EasyAvatar } from "fin-web-components";
import { avatarDisplayWord } from "../../../utils/avatarDisplayWord";
import { StoreOrderApi } from "../../order/StoreOrderApi";
import type { CreateStoreOrderResult } from "../../order/StoreOrderType";
import { StrategyProductPurchaseModal } from "../../store/StrategyProductPurchaseModal";
import type { MembershipProduct, SpaceProfile } from "../SpaceType";
import {
  formatDouyinDisplay,
  formatFollowerDisplay,
  membershipNoRetailPurchaseHint,
} from "../spacePageUtils";
import { SpaInlineCopyRow } from "./SpaInlineCopyRow";
import { SpaceCertLabelTag } from "./SpaceCertLabelTag";

export function SpaceHomeMembershipSection(props: {
  spaceId: string;
  row: MembershipProduct;
  isLoggedIn: boolean;
  onLoginRequest: () => void;
  spaceProfile: SpaceProfile | null;
  subscribeCtaLabel?: string;
  subscribePrimaryButtonClassName?: string;
  subscribeCtaPresentation?: "button" | "textLink";
}): JSX.Element {
  const {
    spaceId,
    row,
    isLoggedIn,
    onLoginRequest,
    spaceProfile,
    subscribeCtaLabel,
    subscribePrimaryButtonClassName,
    subscribeCtaPresentation = "button",
  } = props;
  const retailCta = subscribeCtaLabel?.trim() || "订阅";
  const navigate = useNavigate();
  const [tierId, setTierId] = useState<string | null>(null);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [pendingSign, setPendingSign] = useState(false);

  const isStrategyTool = row.productKind === "strategy_tool";
  const isMarket = isStrategyTool && row.strategySaleMode === "market";
  const isRetail = row.pricingMode?.trim().toLowerCase() === "retail";
  const specs = isRetail ? row.priceSpecs ?? [] : [];
  const canBuyMore =
    isRetail &&
    (isMarket
      ? (row.strategySkuOffers ?? []).some((o) =>
          (o.priceSpecs ?? []).some((s) => s.enabled)
        )
      : specs.some((s) => s.enabled));
  const canBuyRetail = !row.userSubscribed && canBuyMore;

  const openPurchaseModal = () => {
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    if (!row.userSubscribed && !canBuyRetail) {
      message.info(membershipNoRetailPurchaseHint(row));
      return;
    }
    if (row.userSubscribed && !canBuyMore) {
      message.info("暂无可续订规格");
      return;
    }
    if (!isStrategyTool) {
      setTierId(specs.find((s) => s.enabled)?.tierId ?? specs[0]?.tierId ?? null);
    }
    setPurchaseOpen(true);
  };

  const maybeSignAndCreate = useCallback(async (): Promise<CreateStoreOrderResult | string> => {
    const tier = tierId ?? specs[0]?.tierId;
    if (!tier) {
      return "请选择规格";
    }
    let res = await StoreOrderApi.createOrder({
      spaceId,
      spaceProductId: row.spaceProductId,
      serviceTier: tier,
    });
    if (typeof res !== "string" && res.createResult === 401) {
      const needSign = res;
      const tid = row.agreementTemplateId?.trim();
      if (!tid) {
        message.error(needSign.failMessage ?? "请先签署协议");
        return needSign;
      }
      await new Promise<void>((resolve) => {
        Modal.confirm({
          title: "签署购买协议",
          content: needSign.failMessage ?? "需先签署产品购买协议",
          okText: "去签署",
          cancelText: "取消",
          width: 400,
          centered: true,
          className: "lf-modal-square",
          onOk: async () => {
            const signRes = await StoreOrderApi.signAgreement({
              spaceId,
              spaceProductId: row.spaceProductId,
              templateId: tid,
            });
            if (typeof signRes === "string") {
              message.error(signRes);
            } else {
              message.success("签署成功");
            }
            resolve();
          },
          onCancel: () => resolve(),
        });
      });
      res = await StoreOrderApi.createOrder({
        spaceId,
        spaceProductId: row.spaceProductId,
        serviceTier: tier,
      });
    }
    return res;
  }, [row, spaceId, specs, tierId]);

  const confirmPurchase = async () => {
    if (specs.length > 0 && !tierId) {
      message.warning("请先选择规格");
      return;
    }
    setPendingSign(true);
    try {
      const res = await maybeSignAndCreate();
      if (typeof res === "string") {
        return;
      }
      if (res.createResult !== 0 || !res.orderId) {
        message.error(res.failMessage ?? "下单失败");
        return;
      }
      setPurchaseOpen(false);
      navigate(`/checkout/${spaceId}/${encodeURIComponent(res.orderId)}`);
    } finally {
      setPendingSign(false);
    }
  };

  const profile = spaceProfile;
  const spaceName = profile?.name?.trim() ?? "";
  const avatarUrl = profile?.avatarUrl?.trim() ?? "";
  const displaySpaceId = profile?.spaceId ?? spaceId;
  const douyinLine =
    profile != null ? formatDouyinDisplay(profile.douyinId, profile.douyinName) : null;
  const certLabel = profile?.certLabelName?.trim() ?? "";
  const showCertLabel = certLabel !== "";

  const purchaseModal = (
    <Modal
      title={null}
      footer={null}
      closable={false}
      open={purchaseOpen}
      onCancel={() => setPurchaseOpen(false)}
      width="100%"
      centered={false}
      destroyOnClose
      maskClosable
      className="space-purchase-modal space-purchase-modal--fullscreen"
      wrapClassName="space-purchase-modal-wrap"
      styles={{
        wrapper: { padding: 0 },
        content: {
          padding: 0,
          margin: 0,
          maxWidth: "100vw",
          width: "100vw",
          height: "100vh",
          borderRadius: 0,
          overflow: "hidden",
        },
        body: {
          padding: 0,
          height: "100%",
          overflow: "hidden",
        },
      }}
    >
      <div className="space-purchase-modal-shell">
        <aside
          className="space-purchase-modal-space-pane"
          aria-label="空间信息"
        >
          <div className="space-purchase-modal-space-pane__inner">
            <div className="space-purchase-modal-space-pane__avatar-wrap">
              <EasyAvatar
                shape="square"
                squareRadius={10}
                size={104}
                url={avatarUrl !== "" ? avatarUrl : undefined}
                word={avatarDisplayWord(
                  spaceName || row.displayName || "空间",
                  displaySpaceId
                )}
                alt=""
                className="space-purchase-modal-space-pane__avatar"
              />
            </div>
            <div className="space-purchase-modal-space-pane__title-row">
              <span className="space-purchase-modal-space-pane__name">
                {spaceName !== "" ? spaceName : row.displayName || "空间"}
              </span>
              {showCertLabel ? <SpaceCertLabelTag name={certLabel} /> : null}
            </div>
            {profile ? (
              <p className="space-purchase-modal-space-pane__fans">
                {formatFollowerDisplay(profile.followerCount)} 粉丝
              </p>
            ) : null}
            <SpaInlineCopyRow
              text={displaySpaceId}
              successMessage="已复制空间ID"
              ariaLabel="复制空间ID"
              title="复制空间ID"
              className="space-purchase-modal-id-row"
              textClassName="space-purchase-modal-id-nums"
              buttonClassName="space-purchase-modal-id-copy"
            />
            {profile?.signature != null && profile.signature.trim() !== "" ? (
              <p className="space-purchase-modal-space-pane__signature">
                {profile.signature.trim()}
              </p>
            ) : null}
            {douyinLine ? (
              <p className="space-purchase-modal-space-pane__douyin">抖音：{douyinLine}</p>
            ) : null}
          </div>
        </aside>
        <div className="space-purchase-modal-checkout-pane">
          <button
            type="button"
            className="space-purchase-modal-close"
            aria-label="关闭"
            onClick={() => setPurchaseOpen(false)}
          >
            <CloseOutlined />
          </button>
          <div className="space-purchase-modal-checkout-scroll">
            <div className="space-purchase-modal-body__inner">
              <header className="space-purchase-modal-product-block">
                <p className="space-purchase-modal-product-title">{row.displayName}</p>
              </header>
              <section className="space-purchase-modal-intro" aria-label="产品介绍">
                {row.description != null && row.description.trim() !== "" ? (
                  <p>{row.description}</p>
                ) : (
                  <p className="space-purchase-modal-intro-placeholder">暂无详细介绍</p>
                )}
              </section>
              {specs.length > 0 ? (
                <section className="space-purchase-modal-section" aria-label="规格">
                  <p className="space-purchase-modal-label">选择规格</p>
                  <div
                    className="space-purchase-modal-tier-grid"
                    role="group"
                    aria-label="选择规格"
                  >
                    {specs.map((s) => {
                      const active = tierId === s.tierId;
                      return (
                        <button
                          key={s.tierId}
                          type="button"
                          className={
                            active
                              ? "space-purchase-tier-card space-purchase-tier-card--active"
                              : "space-purchase-tier-card"
                          }
                          onClick={() => setTierId(s.tierId)}
                        >
                          <span className="space-purchase-tier-card__name">
                            {s.specName || s.durationLabel}
                          </span>
                          <span className="space-purchase-tier-card__price">
                            ¥{s.priceYuan}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ) : null}
              <p className="space-purchase-modal-product-lead">
                {row.userSubscribed
                  ? "您目前已经在订阅状态，延长权益周期，保持不间断会员权益"
                  : "开通后可按权益使用本空间会员服务"}
              </p>
            </div>
          </div>
          <div className="space-purchase-modal-footer-strip">
            <div className="space-purchase-modal-footer">
              <Button
                className="space-purchase-modal-footer-btn"
                style={{ minWidth: 120 }}
                onClick={() => setPurchaseOpen(false)}
              >
                取消
              </Button>
              <Button
                type="primary"
                className="space-purchase-modal-footer-btn space-purchase-modal-footer-btn--primary"
                style={{ minWidth: 120 }}
                loading={pendingSign}
                onClick={() => void confirmPurchase()}
              >
                立即订购
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );

  const strategyPurchaseModal = isStrategyTool ? (
    <StrategyProductPurchaseModal
      spaceId={spaceId}
      row={row}
      open={purchaseOpen}
      onClose={() => setPurchaseOpen(false)}
      isLoggedIn={isLoggedIn}
      onLoginRequest={onLoginRequest}
      spaceName={spaceName !== "" ? spaceName : row.displayName || "空间"}
      avatarUrl={avatarUrl}
      displaySpaceId={displaySpaceId}
    />
  ) : null;

  if (row.userSubscribed) {
    if (isMarket) {
      const n =
        row.subscribedStrategyCount ??
        row.strategyEntitlements?.filter((e) => e.active).length ??
        0;
      return (
        <>
          <div className="space-m-purchase-col">
            <div className="space-m-actions space-m-actions--subscribed">
              <Tag className="space-m-tag space-m-tag--active">已开通</Tag>
              <span className="space-m-days">已订阅{n}个策略</span>
              {canBuyMore ? (
                <Button
                  type="primary"
                  className="space-m-btn space-m-btn--pill space-m-btn--renew"
                  onClick={openPurchaseModal}
                >
                  订阅更多
                </Button>
              ) : null}
            </div>
          </div>
          {strategyPurchaseModal}
        </>
      );
    }
    const d = row.daysRemaining;
    return (
      <>
        <div className="space-m-purchase-col">
          <div className="space-m-actions space-m-actions--subscribed">
            <Tag className="space-m-tag space-m-tag--active">已开通</Tag>
            {d != null && d >= 0 ? (
              <span className="space-m-days">剩余{d}天</span>
            ) : null}
            {canBuyMore ? (
              <Button
                type="primary"
                className="space-m-btn space-m-btn--pill space-m-btn--renew"
                onClick={openPurchaseModal}
              >
                续订
              </Button>
            ) : null}
          </div>
        </div>
        {isStrategyTool ? strategyPurchaseModal : purchaseModal}
      </>
    );
  }

  return (
    <>
      <div
        className={
          subscribeCtaPresentation === "textLink"
            ? "space-m-purchase-col space-m-purchase-col--inline"
            : "space-m-purchase-col"
        }
      >
        {canBuyRetail ? (
          subscribeCtaPresentation === "textLink" ? (
            <button
              type="button"
              className="space-m-subscribe-text"
              onClick={openPurchaseModal}
            >
              {retailCta}
            </button>
          ) : (
            <Button
              type="primary"
              className={
                subscribePrimaryButtonClassName ??
                "space-m-btn space-m-btn--pill space-m-btn--subscribe"
              }
              onClick={openPurchaseModal}
            >
              {retailCta}
            </Button>
          )
        ) : (
          <span className="space-m-no-spec-hint">{membershipNoRetailPurchaseHint(row)}</span>
        )}
      </div>
      {isStrategyTool ? strategyPurchaseModal : purchaseModal}
    </>
  );
}
